import {
    AbstractControl,
    FormArray,
    ValidationErrors,
    ValidatorFn
} from '@angular/forms';
import { AppPasswordRequirementSettings } from '@models/Settings.model';
import { PASSWORD_CHARSET_REGEX } from '../password-policy';

export class CustomValidators {

    static phone(control: AbstractControl): ValidationErrors | null {
        const value = control.value?.toString().trim();
        // if (!value) return { required: true };
        if (!value) return null; 

        const isValid = /^01[0-9]{8,10}$/.test(value);

        return isValid ? null : { invalidPhone: true };
    }

    /**
     * Validates a NEW password against the tenant policy
     * (/settings/public → passwordRequirement).
     *
     * Takes a getter (not a snapshot) so the freshest settings apply even when
     * the form is built before the settings response lands. Empty values pass —
     * pair with Validators.required where the field is mandatory.
     *
     * Emits standard `minlength`/`maxlength` errors plus one flag per unmet
     * character rule: `passwordUpperCase`, `passwordLowerCase`,
     * `passwordNumber`, `passwordSymbol`.
     */
    static passwordRequirement(policy: () => AppPasswordRequirementSettings): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value: string = control.value ?? '';
            if (!value) return null;

            const req = policy();
            const errors: ValidationErrors = {};

            if (value.length < req.passwordMinLength) {
                errors['minlength'] = {
                    requiredLength: req.passwordMinLength,
                    actualLength: value.length
                };
            }
            if (value.length > req.passwordMaxLength) {
                errors['maxlength'] = {
                    requiredLength: req.passwordMaxLength,
                    actualLength: value.length
                };
            }
            if (req.passwordRequireUpperCase && !PASSWORD_CHARSET_REGEX.upperCase.test(value)) {
                errors['passwordUpperCase'] = true;
            }
            if (req.passwordRequireLowerCase && !PASSWORD_CHARSET_REGEX.lowerCase.test(value)) {
                errors['passwordLowerCase'] = true;
            }
            if (req.passwordRequireNumber && !PASSWORD_CHARSET_REGEX.number.test(value)) {
                errors['passwordNumber'] = true;
            }
            if (req.passwordRequireSymbol && !PASSWORD_CHARSET_REGEX.symbol.test(value)) {
                errors['passwordSymbol'] = true;
            }

            return Object.keys(errors).length ? errors : null;
        };
    }

    /**
     * Length-only variant for the LOGIN password. Character rules are skipped
     * on purpose: an existing password created under an older policy must
     * still be enterable.
     */
    static passwordLength(policy: () => AppPasswordRequirementSettings): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value: string = control.value ?? '';
            if (!value) return null;

            const req = policy();

            if (value.length < req.passwordMinLength) {
                return {
                    minlength: {
                        requiredLength: req.passwordMinLength,
                        actualLength: value.length
                    }
                };
            }
            if (value.length > req.passwordMaxLength) {
                return {
                    maxlength: {
                        requiredLength: req.passwordMaxLength,
                        actualLength: value.length
                    }
                };
            }
            return null;
        };
    }

    // Confirm password match with password
    static matchPassword(controlName: string, matchingControlName: string): ValidatorFn {
        return (group: AbstractControl): ValidationErrors | null => {
            const control = group.get(controlName);
            const matchingControl = group.get(matchingControlName);
            if (!control || !matchingControl) return null;

            if (control.value !== matchingControl.value) {
                matchingControl.setErrors({ passwordsMismatch: true });
                return { passwordsMismatch: true };
            } else {
                const errors = matchingControl.errors;
                if (errors) delete errors['passwordsMismatch'];
                if (Object.keys(errors ?? {}).length === 0) matchingControl.setErrors(null);
                return null;
            }
        };
    }


    static minArrayLengthValidator(min: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control instanceof FormArray) {
                return control.length >= min ? null : { minArrayLength: { requiredLength: min } };
            }
            return null;
        };
    }

}
